/**
 * Envoltorio del SDK de Telegram WebApp.
 *
 * Todo lo que toca `window.Telegram` pasa por aqui, por dos razones:
 *
 *  1. En el escritorio, fuera de Telegram, `window.Telegram` no existe. El juego
 *     tiene que poder jugarse en local sin Telegram (modo DEV del spec), asi que
 *     cada llamada comprueba antes y, si no hay Telegram, no hace nada en vez de
 *     reventar.
 *  2. La API de Telegram cambia y anade metodos por version. Llamar a uno que no
 *     existe en la version del cliente del jugador lanza una excepcion que
 *     mataria la partida. Aqui se comprueba la version antes.
 */

interface TgTema {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface TgUsuario {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: { user?: TgUsuario; start_param?: string };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TgTema;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
  lockOrientation?(): void;
  requestFullscreen?(): void;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void };
  MainButton: {
    setText(t: string): void;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(estilo: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(tipo: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  switchInlineQuery?(consulta: string, tipos?: string[]): void;
  shareToStory?(media: string, params?: Record<string, unknown>): void;
  openTelegramLink?(url: string): void;
  openLink?(url: string, opciones?: { try_instant_view?: boolean }): void;
  onEvent(evento: string, cb: () => void): void;
}

function wa(): TgWebApp | null {
  const t = (window as unknown as { Telegram?: { WebApp?: TgWebApp } }).Telegram;
  return t?.WebApp ?? null;
}

/** Compara la version del cliente con la minima que necesita un metodo. */
function versionAlMenos(minima: string): boolean {
  const app = wa();
  if (!app) return false;
  const a = app.version.split('.').map(Number);
  const b = minima.split('.').map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return true;
}

export const telegram = {
  /**
   * true si el juego corre de verdad DENTRO de Telegram.
   *
   * OJO con la trampa: `window.Telegram.WebApp` existe en cuanto se carga el
   * script del SDK, tambien en un navegador normal (se comprobo el 30-sep-2026:
   * fuera de Telegram da version "6.0" y platform "unknown"). Lo que NO existe
   * fuera de Telegram es `initData`, que es la cadena firmada con el token del
   * bot. Por eso la pregunta correcta es si hay initData, no si hay objeto.
   *
   * Y es la misma senal que valida el servidor, asi que cliente y servidor
   * estan de acuerdo en que significa "dentro de Telegram".
   */
  get disponible(): boolean {
    const app = wa();
    return app !== null && app.initData.length > 0;
  },

  /** true si el script del SDK esta cargado, este o no dentro de Telegram. */
  get sdkPresente(): boolean {
    return wa() !== null;
  },

  /** initData crudo. Es lo que el servidor valida con HMAC. */
  get initData(): string {
    return wa()?.initData ?? '';
  },

  get usuario(): TgUsuario | null {
    return wa()?.initDataUnsafe.user ?? null;
  },

  /**
   * El parametro de startapp: `ref_123`, `duel_456`, `src_canalX`.
   * De aqui salen los referidos, los duelos compartidos y la atribucion.
   */
  get parametroInicio(): string {
    return wa()?.initDataUnsafe.start_param ?? '';
  },

  /** Idioma del cliente de Telegram. El spec pide es por defecto. */
  get idioma(): string {
    return wa()?.initDataUnsafe.user?.language_code ?? 'es';
  },

  get esPremium(): boolean {
    return wa()?.initDataUnsafe.user?.is_premium ?? false;
  },

  get tema(): TgTema {
    return wa()?.themeParams ?? {};
  },

  get esquemaColor(): 'light' | 'dark' {
    return wa()?.colorScheme ?? 'dark';
  },

  /** Arranque: avisar a Telegram, expandir y bloquear el gesto de cierre. */
  preparar(): void {
    const app = wa();
    if (!app) return;
    app.ready();
    app.expand();

    // Sin esto, el swipe hacia abajo del juego cierra la Mini App. Es de la
    // version 7.7; en clientes viejos no existe y llamarlo lanzaria excepcion.
    if (versionAlMenos('7.7') && app.disableVerticalSwipes) {
      app.disableVerticalSwipes();
    }
    // El juego es oscuro: que la cabecera no sea blanca y haga de marco.
    if (versionAlMenos('6.1') && app.setHeaderColor) {
      app.setHeaderColor('#05060f');
    }
    if (versionAlMenos('6.1') && app.setBackgroundColor) {
      app.setBackgroundColor('#05060f');
    }
  },

  /** Alto util del area visible. Con teclado abierto o barras, cambia. */
  get altoViewport(): number {
    return wa()?.viewportStableHeight ?? window.innerHeight;
  },

  alCambiarViewport(cb: () => void): void {
    wa()?.onEvent('viewportChanged', cb);
  },

  alCambiarTema(cb: () => void): void {
    wa()?.onEvent('themeChanged', cb);
  },

  // --- Boton de atras ---

  mostrarAtras(cb: () => void): void {
    const app = wa();
    // BackButton es de la version 6.1. En clientes viejos (y fuera de Telegram,
    // donde el SDK dice ser 6.0) llamarlo solo llena la consola de avisos.
    if (!app || !versionAlMenos('6.1')) return;
    app.BackButton.onClick(cb);
    app.BackButton.show();
  },

  ocultarAtras(): void {
    if (!versionAlMenos('6.1')) return;
    wa()?.BackButton.hide();
  },

  // --- Haptics: lo que hace que un movil se sienta como un mando ---

  vibrar(estilo: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'): void {
    try {
      wa()?.HapticFeedback.impactOccurred(estilo);
    } catch {
      // En algunos clientes de escritorio no existe. No pasa nada.
    }
  },

  avisar(tipo: 'error' | 'success' | 'warning'): void {
    try {
      wa()?.HapticFeedback.notificationOccurred(tipo);
    } catch {
      /* igual */
    }
  },

  seleccion(): void {
    try {
      wa()?.HapticFeedback.selectionChanged();
    } catch {
      /* igual */
    }
  },

  // --- Compartir: el motor viral ---

  /**
   * Abre el selector de chats de Telegram con una consulta inline. Es el camino
   * de "retar a un amigo": el bot responde con la tarjeta del duelo.
   */
  compartirInline(consulta: string): void {
    const app = wa();
    if (!app?.switchInlineQuery) return;
    app.switchInlineQuery(consulta, ['users', 'groups']);
  },

  /**
   * Si este cliente puede compartir en Stories. Version 7.8 o superior.
   *
   * Se pregunta ANTES de pedirle al servidor que genere la imagen: generar una
   * tarjeta que luego no se va a poder compartir es trabajo tirado, y en un
   * movil lento es un segundo de espera para nada.
   */
  get puedeCompartirHistoria(): boolean {
    return versionAlMenos('7.8') && typeof wa()?.shareToStory === 'function';
  },

  /** Comparte una imagen en las Stories de Telegram. Version 7.8 o superior. */
  compartirHistoria(urlMedia: string, params?: Record<string, unknown>): void {
    const app = wa();
    if (!versionAlMenos('7.8') || !app?.shareToStory) return;
    app.shareToStory(urlMedia, params);
  },

  abrirEnlaceTelegram(url: string): void {
    const app = wa();
    if (app?.openTelegramLink) app.openTelegramLink(url);
    else window.open(url, '_blank');
  },

  abrirEnlace(url: string): void {
    const app = wa();
    if (app?.openLink) app.openLink(url);
    else window.open(url, '_blank');
  },

  cerrar(): void {
    wa()?.close();
  },
};
